import React, { useRef, useState } from 'react';
import { api, ApiError, newKey, RECEIPT_TYPES, uploadReceipt } from '../lib/api';
import { addDays, money, today } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { CompleteResult, LeaseDraft, Task } from '../lib/types';
import { Drawer, Loading, type Ctx } from '../main';

/**
 * Completing a task.
 *
 * This component knows that *a* question may exist. It does not know what any of
 * them say, which resolutions are available, or what each one creates — all of
 * that comes from the rule the server served. Adding a branch to the engine needs
 * no change here and no release of this console.
 *
 * If you find yourself about to write a task name or a resolution label in this
 * file, that is the signal something is wrong with the API, not with the client.
 */
export function CompleteDrawer({ ctx, task, onClose, onDone }: {
  ctx: Ctx;
  task: Task;
  onClose: () => void;
  onDone: (r: CompleteResult) => Promise<void>;
}): React.JSX.Element {
  const rule = ctx.ruleFor(task.name);
  const isMoney = task.type !== 'Action';

  const [resolution, setResolution] = useState<string | null>(null);
  const [amount, setAmount] = useState(String(task.amount || ''));
  const [method, setMethod] = useState('BankTransfer');
  const [paidOn, setPaidOn] = useState(today());
  const [receiptId, setReceiptId] = useState<string | null>(null);
  const [receiptName, setReceiptName] = useState<string | null>(null);
  const [setup, setSetup] = useState({
    rooms: '4', unitInstalments: '3', cleaningInstalments: '2',
    securityInstalments: '1', idCardInstalments: '1', brokerCommission: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [lease, setLease] = useState<LeaseForm>(() => blankLease());

  // minted when the drawer opens, so every retry of this one intent carries it
  const key = useRef(newKey());

  const chosen = rule?.question?.options.find((o) => o.label === resolution) ?? null;

  const attach = async (file: File): Promise<void> => {
    setBusy(true); setError(null);
    try {
      setReceiptId(await uploadReceipt(file));
      setReceiptName(file.name);
    } catch (e) {
      setError(`Could not upload the receipt: ${(e as Error).message}`);
    } finally { setBusy(false); }
  };

  const ready =
    (!rule?.question || resolution !== null) &&
    (!isMoney || (Number(amount) > 0 && receiptId !== null)) &&
    (!chosen?.createsLease || leaseReady(lease));

  const confirm = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      const body: Record<string, unknown> = { expectedVersion: task.version };
      if (rule?.question) body.resolution = resolution;
      if (isMoney) {
        body.amount = Number(amount);
        body.method = method;
        body.paidOn = paidOn;
        body.receiptId = receiptId;
      }
      if (chosen?.createsLease) body.lease = await leaseBody(lease, setLease);
      if (rule?.special === 'contract-setup') {
        body.contractSetup = {
          rooms: Number(setup.rooms),
          unitInstalments: Number(setup.unitInstalments),
          cleaningInstalments: Number(setup.cleaningInstalments),
          securityInstalments: Number(setup.securityInstalments),
          idCardInstalments: Number(setup.idCardInstalments),
          brokerCommission: setup.brokerCommission,
        };
      }
      let result: CompleteResult;
      try {
        result = await api.completeTask(task.id, body, key.current);
      } catch (e) {
        // the same intent is still running (a double click): wait, then replay
        // with the same key, which returns the stored result
        if ((e as ApiError).code !== 'request_in_flight') throw e;
        await new Promise((r) => setTimeout(r, 1500));
        result = await api.completeTask(task.id, body, key.current);
      }
      await onDone(result);
    } catch (e) {
      const err = e as ApiError;
      // version_conflict means a colleague completed it first. The server is
      // authoritative: show that rather than letting two people believe different
      // things. Other 409s (a receipt already used elsewhere) say what to fix.
      setError(err.code === 'version_conflict'
        ? 'Someone else completed this first. Close this and reload — the server is right.'
        : err.message);
      setBusy(false);
    }
  };

  return (
    <Drawer
      title={task.name}
      subtitle={`${ctx.propertyName(task.propertyId)} · ${task.workflow}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!ready || busy} onClick={confirm}>
            {busy ? 'Running…' : 'Complete task'}
          </button>
        </>
      }
    >
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}

      {isMoney && (
        <>
          <h3 className="sec" style={{ fontSize: 13 }}>
            {task.type === 'Income' ? 'Record the payment' : 'Record the cost'}
          </h3>
          <div className="frow">
            <div className="field">
              <label htmlFor="amount">Amount ({task.currency})</label>
              {/* a price that was set is a manager's to change; the API holds it too */}
              <input id="amount" type="number" min={0} value={amount}
                readOnly={!ctx.canManage && task.amount > 0}
                onChange={(e) => setAmount(e.target.value)} />
              {!ctx.canManage && task.amount > 0 ? (
                <div className="hint">Set when the task was created. A manager can change it.</div>
              ) : task.amount > 0 && Number(amount) !== task.amount && (
                <div className="hint">Expected {money(task.amount, task.currency)}</div>
              )}
            </div>
            <div className="field">
              <label htmlFor="paid">{task.type === 'Income' ? 'Received on' : 'Paid on'}</label>
              <input id="paid" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="method">Method</label>
            <select id="method" value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="BankTransfer">Bank transfer</option>
              <option value="Cash">Cash</option>
              <option value="Cheque">Cheque</option>
              <option value="CreditCard">Credit card</option>
            </select>
          </div>
          <div className="field">
            <label>Receipt</label>
            <input ref={fileInput} type="file" accept={RECEIPT_TYPES} hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void attach(f); }} />
            <button className={`btn full ${receiptId ? '' : 'accent'}`} disabled={busy}
              onClick={() => fileInput.current?.click()}>
              {busy ? 'Uploading…' : receiptId ? `Attached: ${receiptName} — replace` : 'Attach a receipt'}
            </button>
            <div className="hint">
              Required. A money task cannot close without evidence — that rule is enforced by the
              engine and by the database, not by this form.
            </div>
          </div>
        </>
      )}

      {rule?.question && (
        <>
          <h3 className="sec" style={{ fontSize: 13 }}>{rule.question.prompt}</h3>
          {rule.question.options.map((option) => (
            <button key={option.label}
              className={`opt ${resolution === option.label ? 'on' : ''}`}
              onClick={() => setResolution(option.label)}>
              <span className="k" />
              <span>
                <b>{option.label}</b>
                <span>
                  {option.detail ??
                    (option.next.length
                      ? `Creates: ${option.next.join(', ')}`
                      : 'Nothing further is created — the chain ends here.')}
                </span>
                {(option.pauseRent || option.resumeRent || option.createsLease) && (
                  <span style={{ marginTop: 6 }}>
                    {option.pauseRent && <span className="tag t-bad">pauses rent</span>}
                    {option.resumeRent && <span className="tag t-ok">resumes rent</span>}
                    {option.createsLease && <span className="tag t-warn">creates a lease</span>}
                  </span>
                )}
              </span>
            </button>
          ))}
        </>
      )}

      {rule?.special === 'contract-setup' && (
        <>
          <h3 className="sec" style={{ fontSize: 13 }}>Complete the property record</h3>
          <div className="note warn">
            These numbers decide how many cost tasks the engine creates. Instalments become one
            task each; getting them wrong here means correcting them one at a time later.
          </div>
          <div className="frow" style={{ marginTop: 12 }}>
            {([
              ['rooms', 'Rooms'],
              ['unitInstalments', 'Unit instalments'],
              ['cleaningInstalments', 'Cleaning instalments'],
              ['securityInstalments', 'Security instalments'],
              ['idCardInstalments', 'ID card instalments'],
            ] as const).map(([field, label]) => (
              <div className="field" key={field}>
                <label htmlFor={`cs-${field}`}>{label}</label>
                <input id={`cs-${field}`} type="number" min={0} value={setup[field]}
                  onChange={(e) => setSetup({ ...setup, [field]: e.target.value })} />
              </div>
            ))}
          </div>
          <label className="btn full" style={{ justifyContent: 'flex-start', gap: 10 }}>
            <input type="checkbox" checked={setup.brokerCommission}
              onChange={(e) => setSetup({ ...setup, brokerCommission: e.target.checked })} />
            Broker commission applies
          </label>
        </>
      )}

      {chosen?.createsLease && (
        <LeaseBlock ctx={ctx} task={task} value={lease} onChange={setLease} />
      )}

      {!isMoney && !rule?.question && !rule?.special && (
        <div className="note ok" style={{ marginTop: 14 }}>
          Nothing else to record.
          {rule?.next.length
            ? ` Completing this creates: ${rule.next.join(', ')}.`
            : ' This ends the chain.'}
        </div>
      )}
    </Drawer>
  );
}

/* ------------------------------------------------------------- the lease */

interface LeaseForm {
  who: 'registered' | 'new';
  tenantId: string;
  newTenant: { name: string; phone: string; email: string; nationality: string };
  /** set once a new tenant has been saved, so a retry does not create them twice */
  createdTenantId: string | null;
  roomId: string; startDate: string; endDate: string; monthlyRent: string; depositAmount: string;
}

const oneYearOn = (start: string): string => {
  const d = new Date(`${start}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return addDays(d.toISOString().slice(0, 10), -1);
};

function blankLease(): LeaseForm {
  const start = addDays(today(), 5);
  return {
    who: 'registered', tenantId: '', createdTenantId: null,
    newTenant: { name: '', phone: '', email: '', nationality: '' },
    roomId: '', startDate: start, endDate: oneYearOn(start), monthlyRent: '', depositAmount: '',
  };
}

function leaseReady(l: LeaseForm): boolean {
  const who = l.who === 'registered' ? Boolean(l.tenantId)
    : Boolean(l.createdTenantId || (l.newTenant.name.trim() && l.newTenant.phone.trim() && l.newTenant.email.includes('@')));
  return who && Boolean(l.roomId) && l.endDate > l.startDate && Number(l.monthlyRent) > 0;
}

/** A new tenant is saved first — the lease needs their id — and remembered, so a retry reuses them. */
async function leaseBody(l: LeaseForm, update: (l: LeaseForm) => void): Promise<LeaseDraft> {
  let tenantId = l.who === 'registered' ? l.tenantId : l.createdTenantId;
  if (!tenantId) {
    const { tenant } = await api.createTenant({
      name: l.newTenant.name.trim(), phone: l.newTenant.phone, email: l.newTenant.email.trim(),
      ...(l.newTenant.nationality ? { nationality: l.newTenant.nationality } : {}),
    });
    tenantId = tenant.id;
    update({ ...l, createdTenantId: tenant.id });
  }
  return {
    tenantId, roomId: l.roomId, startDate: l.startDate, endDate: l.endDate,
    monthlyRent: Number(l.monthlyRent), depositAmount: Number(l.depositAmount || 0),
  };
}

/**
 * The lease this deal creates.
 *
 * The rent agreed here becomes the room's current rent, read-only everywhere
 * else: occupancy and rent belong to the lease, not to the room.
 */
function LeaseBlock({ ctx, task, value, onChange }: {
  ctx: Ctx; task: Task; value: LeaseForm; onChange: (l: LeaseForm) => void;
}): React.JSX.Element {
  // The tenant list and the rents are a manager's (the API refuses them to a
  // specialist), and so is the decision a lease is. Said here, before a form a
  // specialist could not finish.
  if (!ctx.canManage) {
    return (
      <div className="note warn">
        Starting a lease is a manager’s decision — it sets the rent and books the deposit. Ask a manager
        to complete this task, or reassign it to one.
      </div>
    );
  }
  return <LeaseForm_ ctx={ctx} task={task} value={value} onChange={onChange} />;
}

function LeaseForm_({ ctx, task, value, onChange }: {
  ctx: Ctx; task: Task; value: LeaseForm; onChange: (l: LeaseForm) => void;
}): React.JSX.Element {
  const rooms = useResource(() => api.rooms(task.propertyId), [task.propertyId]);
  const tenants = useResource(() => api.tenants(), []);
  const vacant = (rooms.data?.rooms ?? []).filter((r) => r.status === 'Vacant');
  const free = (tenants.data?.tenants ?? []).filter((t) => !t.lease_id);
  const set = (patch: Partial<LeaseForm>): void => onChange({ ...value, ...patch });

  const pickRoom = (id: string): void => {
    const room = vacant.find((r) => r.id === id);
    const rent = room?.askingLow ? String(room.askingLow) : value.monthlyRent;
    set({ roomId: id, monthlyRent: rent, depositAmount: value.depositAmount || rent });
  };

  return (
    <>
      <h3 className="sec" style={{ fontSize: 13 }}>The new lease</h3>
      {(rooms.loading || tenants.loading) && <Loading />}

      <div className="field">
        <label htmlFor="l-who">Who is the tenant?</label>
        <select id="l-who" value={value.who}
          onChange={(e) => set({ who: e.target.value as LeaseForm['who'] })}>
          <option value="registered">Someone already registered</option>
          {ctx.canManage && <option value="new">Someone new</option>}
        </select>
      </div>

      {value.who === 'registered' ? (
        <div className="field">
          <label htmlFor="l-tenant">Tenant</label>
          <select id="l-tenant" value={value.tenantId} onChange={(e) => set({ tenantId: e.target.value })}>
            <option value="">— choose —</option>
            {free.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.phone}</option>)}
          </select>
          {tenants.data && free.length === 0 && (
            <div className="hint">Everyone registered already has a lease. Choose “Someone new”.</div>
          )}
        </div>
      ) : value.createdTenantId ? (
        <div className="note ok" style={{ marginBottom: 12 }}>
          {value.newTenant.name} is registered. They sign in with {value.newTenant.phone}.
        </div>
      ) : (
        <>
          <div className="frow">
            <div className="field">
              <label htmlFor="l-name">Full name</label>
              <input id="l-name" value={value.newTenant.name}
                onChange={(e) => set({ newTenant: { ...value.newTenant, name: e.target.value } })} />
            </div>
            <div className="field">
              <label htmlFor="l-phone">Phone</label>
              <input id="l-phone" type="tel" placeholder="+971 50 123 4567" value={value.newTenant.phone}
                onChange={(e) => set({ newTenant: { ...value.newTenant, phone: e.target.value } })} />
            </div>
          </div>
          <div className="frow">
            <div className="field">
              <label htmlFor="l-email">Email</label>
              <input id="l-email" type="email" value={value.newTenant.email}
                onChange={(e) => set({ newTenant: { ...value.newTenant, email: e.target.value } })} />
            </div>
            <div className="field">
              <label htmlFor="l-nat">Nationality (optional)</label>
              <input id="l-nat" value={value.newTenant.nationality}
                onChange={(e) => set({ newTenant: { ...value.newTenant, nationality: e.target.value } })} />
            </div>
          </div>
          <div className="hint" style={{ marginTop: -6, marginBottom: 12 }}>
            They sign in with their email, and confirm it and the phone the first time.
          </div>
        </>
      )}

      <div className="field">
        <label htmlFor="l-room">Room</label>
        <select id="l-room" value={value.roomId} onChange={(e) => pickRoom(e.target.value)}>
          <option value="">{vacant.length ? '— choose a vacant room —' : 'No vacant rooms'}</option>
          {vacant.map((r) => (
            <option key={r.id} value={r.id}>
              Room {r.number} · asking {money(r.askingLow)}–{money(r.askingHigh).replace('AED ', '')}
            </option>
          ))}
        </select>
      </div>
      <div className="frow">
        <div className="field">
          <label htmlFor="l-start">Lease start</label>
          <input id="l-start" type="date" value={value.startDate}
            onChange={(e) => set({ startDate: e.target.value, endDate: oneYearOn(e.target.value) })} />
        </div>
        <div className="field">
          <label htmlFor="l-end">Lease end</label>
          <input id="l-end" type="date" value={value.endDate} onChange={(e) => set({ endDate: e.target.value })} />
        </div>
      </div>
      <div className="frow">
        <div className="field">
          <label htmlFor="l-rent">Monthly rent (AED)</label>
          <input id="l-rent" type="number" min={0} value={value.monthlyRent}
            onChange={(e) => set({ monthlyRent: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="l-dep">Deposit (AED)</label>
          <input id="l-dep" type="number" min={0} value={value.depositAmount}
            onChange={(e) => set({ depositAmount: e.target.value })} />
        </div>
      </div>
      <div className="hint">
        The rent agreed here becomes the room’s current rent. The first month is pro-rated from the
        start date; the deposit becomes a task of its own.
      </div>
    </>
  );
}
